import i18next, { t } from "i18next";
import { z } from "zod";
import { env } from "@/env";
import { requestAdminStepUp } from "@/lib/admin-access";

// Functions, not constants: the text follows the current interface language.
export const impersonationReadOnlyMessage =
  "This session is read-only. Stop impersonating to make changes.";
export const accountUnavailableMessage = (): string =>
  t("auth.native.errors.accountUnavailable");
export const authProviderUnavailableMessage = (): string =>
  t("auth.native.errors.providerUnavailable");
export const nativeSocialRedirectSchema = z.object({
  url: z.url({ protocol: /^https$/ }),
});
export const nativeSessionSchema = z.object({
  user: z.object({
    id: z.string(),
    email: z.email(),
    emailVerified: z.boolean(),
    twoFactorEnabled: z.boolean().optional(),
    hasPassword: z.boolean().optional(),
  }),
  enrollmentRequired: z.boolean(),
  mfaRequired: z.boolean(),
  role: z.string().nullable().optional(),
  impersonatedBy: z.string().nullable().optional(),
  adminStepUpExpiresIn: z.number().int().nonnegative().optional(),
  hasTotp: z.boolean().optional(),
  hasPasskey: z.boolean().optional(),
});
export type NativeSession = z.infer<typeof nativeSessionSchema>;
export type AppIdentity = {
  uid: string;
  email: string | null;
  emailVerified: boolean;
  displayName: string | null;
  providerData: { providerId: string }[];
};
let identity: AppIdentity | null = null;
export const getNativeIdentity = () => identity;
export const setNativeIdentity = (user: AppIdentity | null) => {
  identity = user;
};

// Keeps the HTTP status and server code so screens can explain, for
// example, that a security change needs a recent sign-in.
export class NativeAuthRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
  ) {
    super(message);
    this.name = "NativeAuthRequestError";
  }
}

// When this tab's sign-up began; bounds the post-sign-up passkey prompt to
// the server's fresh-session window.
export const SIGN_UP_STARTED_KEY = "rosc.native.signUpStartedAt";

// Our proxy says 401 RECENT_AUTHENTICATION_REQUIRED; Better Auth's own
// fresh-session check (e.g. adding a passkey) says 403 SESSION_NOT_FRESH.
export const needsRecentSignIn = (error: unknown): boolean =>
  error instanceof NativeAuthRequestError &&
  ((error.status === 401 && error.code === "RECENT_AUTHENTICATION_REQUIRED") ||
    (error.status === 403 && error.code === "SESSION_NOT_FRESH"));

// The display language lives only in this browser, so account emails
// (sign-in code, verification, password) learn it from Accept-Language.
export function displayLanguageHeader(): Record<string, string> {
  const language = i18next.resolvedLanguage ?? i18next.language;
  return language && /^[A-Za-z]{2,3}([-_][A-Za-z0-9]{1,8})*$/.test(language)
    ? { "Accept-Language": language }
    : {};
}

export async function nativeAuthRequest(
  path: string,
  body?: unknown,
  options: Pick<RequestInit, "keepalive" | "signal"> = {},
): Promise<unknown> {
  const response = await fetch(
    new URL(`/api/auth/${path}`, env.VITE_API_V2_BASE_URL),
    {
      ...options,
      method: body === undefined ? "GET" : "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...displayLanguageHeader(),
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      cache: "no-store",
    },
  );
  if (!response.ok) {
    // Errors come as { code } from the auth engine or { error: { code } }
    // from the ROSC runtime.
    const { code } = await response
      .json()
      .then((data) =>
        z
          .union([
            z.object({ code: z.string() }),
            z
              .object({ error: z.object({ code: z.string() }) })
              .transform((value) => value.error),
          ])
          .parse(data),
      )
      .catch(() => ({ code: "" }));
    if (code === "ADMIN_STEP_UP_REQUIRED") requestAdminStepUp();
    if (
      path === "session" &&
      response.status === 401 &&
      code !== "ACCOUNT_UNAVAILABLE" &&
      code !== "ADMIN_STEP_UP_REQUIRED"
    )
      return null;
    const fail = (message: string) =>
      new NativeAuthRequestError(message, response.status, code);
    if (response.status === 503 && code === "AUTH_PROVIDER_UNAVAILABLE")
      throw fail(authProviderUnavailableMessage());
    if (code === "ACCOUNT_UNAVAILABLE") throw fail(accountUnavailableMessage());
    if (code === "IMPERSONATION_READ_ONLY")
      throw fail(impersonationReadOnlyMessage);
    if (code === "PROVIDER_NOT_FOUND")
      throw fail(t("auth.native.errors.providerNotFound"));
    if (response.status === 403) throw fail(t("auth.native.errors.forbidden"));
    if (response.status === 409) throw fail(t("auth.native.errors.migration"));
    throw fail(t("auth.native.errors.unknown"));
  }
  return response.status === 204 ? null : response.json();
}

export async function renewNativeSession(): Promise<void> {
  await nativeAuthRequest("client/renew", {});
}

export async function getRealtimeToken(): Promise<string> {
  const response = z
    .object({ token: z.string().min(1) })
    .parse(await nativeAuthRequest("realtime-token", {}));
  return response.token;
}
